import { ICard, TokenUtil, GameToken, ICardReference } from "./Card";
import { IDeck } from "./Deck";
import { IAppState } from "./App";
import { DeckUtil } from "./DeckUtil";
import { CardUtil } from "./CardUtil";

export interface ICampaign {
    name: string;
    generateChaosBag: (difficulty: GameDifficulty) => ChaosToken[];
    intro: IThemeText;
    scenarios: IScenario[];
}
export enum GameDifficulty {
    Easy = 1, Standard = 2, Hard = 3, Expert = 4
}
export enum ChaosToken {
    Plus1,
    Zero,
    Minus1,
    Minus2,
    Minus3,
    Minus4,
    Minus5,
    Minus6,
    Minus7,
    Minus8,
    Skull,
    Cultist,
    Tablet,
    ElderThing,
    ElderSign,
    AutoFail
}
export interface IScenarioSettings {
    investigators: IInvestigatorInPlay[];
    difficulty: GameDifficulty;
}
export enum ScenarioType {
    Scenario,
    Interlude
}
export interface IScenario {
    name: string;
    type?: ScenarioType;
    getIntro: (state: IScenarioState) => IThemeText;
    setup: (state: IScenarioState) => void;
    resolutions: IResolution[];
}
export interface IQuestion {
    question: string;
    choices: string[];
}
export interface IResolution {
    text: IThemeText;
    complete: (state: IScenarioState) => boolean;
    questions?: IQuestion[];
    completeQuestions?: (state: IScenarioState, answers: number[]) => void;
}
export interface IAction {
    title: string;
    action?: (appState: IAppState) => void;
}
export interface IThemeText {
    title: string;
    texts: string[];
    actions?: IAction[];
}
export class ThemeTextUtil {

    static wrap(themeText: IThemeText, callback: (appState: IAppState) => void) {
        return {
            title: themeText.title,
            texts: themeText.texts,
            actions: themeText.actions.map((a) => {
                return {
                    title: a.title, action: (appState: IAppState) => {
                        if (a.action) a.action(appState);
                        callback(appState);
                    }
                }
            })
        };
    }
}
export interface ILocation {
    card: ICard;
    inactive: boolean;
    enemies?: IDeck;
}
export interface IRoundCounter {
    round: number;
    phase: number;
}
export interface IScenarioState {
    campaignIndex: number;
    scenarioIndex: number;
    nextScenarioIndex?: number;
    scenarioSetupCompleted: boolean;
    difficulty: GameDifficulty;
    scenarioCard: ICard,
    actState: IActState;
    agendaState: IAgendaState;
    currentSelectedInvestigator: number;
    investigators: IInvestigatorInPlay[];
    currentSelectedLocationId?: number;
    locations: ILocation[];
    chaosBag: ChaosToken[];
    encounterDeck: IDeck;
    discardDeck?: IDeck;
    victoryDisplay?:IDeck;
    extraDecks?: IDeck[];
    activeOtherComponent?: OtherComponent;
    activeOtherComponentIndex?: number;

    campaignNotes?: ICampaignNoteGroup[];
    resolution?: IResolution;
    killedAndInsaneInvestigators?: IInvestigatorInPlay[];
    scenarioLog?: string[];
    scenarioSuccessful?: boolean;
    roundCounter?: IRoundCounter;
    campaignSetup?: boolean;
}
export interface ICampaignNoteGroup {
    title: string;
    notes: ICampaignNote[];
}
export interface ICampaignNote {
    note: string;
    crossedOut?: boolean;
    hidden?: boolean;
}
export class ScenarioState implements IScenarioState {
    campaignIndex: number;
    scenarioIndex: number;
    scenarioSetupCompleted: boolean;
    difficulty: GameDifficulty;
    scenarioCard: ICard;
    actState: IActState;
    agendaState: IAgendaState;
    currentSelectedInvestigator: number;
    investigators: IInvestigatorInPlay[];
    currentSelectedLocationId?: number;
    locations: ILocation[];
    chaosBag: ChaosToken[];
    encounterDeck: IDeck;
    discardDeck?: IDeck;
    victoryDisplay?: IDeck;
    outOfPlayCards: ICard[];
    activeOtherComponent?: OtherComponent;
    killedAndInsaneInvestigators?: IInvestigatorInPlay[];
    constructor(campaignIndex: number, scenarioIndex?: number) {
        this.campaignIndex = campaignIndex;
        this.scenarioIndex = 0;
        if (scenarioIndex)
            this.scenarioIndex = scenarioIndex;
    }

}
export class ScenarioUtil {
    static campaignNotesTitle = "Campaign Notes";
    static ensureCampaignNoteGroup(state: IScenarioState, title?: string): ICampaignNoteGroup {
        if (!title) {
            title = ScenarioUtil.campaignNotesTitle;
        }
        if (!state.campaignNotes || !state.campaignNotes.length) state.campaignNotes = [{ title: ScenarioUtil.campaignNotesTitle, notes: [] }];
        const groups = state.campaignNotes.filter(cng => cng.title === title);
        if (groups && groups.length)
            return groups[0];
        const group: ICampaignNoteGroup = { title: title, notes: [] };
        state.campaignNotes.push(group);
        return group;
    }
    static addCampaignNote(state: IScenarioState, note: string, groupTitle?: string): ICampaignNote {
        const group = this.ensureCampaignNoteGroup(state, groupTitle);
        const campaignNote: ICampaignNote = { note: note };
        group.notes.push(campaignNote);
        return campaignNote;
    }
    static getCampaignNote(state: IScenarioState, note: string): ICampaignNote {
        if (!state.campaignNotes || !state.campaignNotes.length) return null;
        for (let ig = 0; ig < state.campaignNotes.length; ig++) {
            const g = state.campaignNotes[ig];
            const notes = g.notes.filter(cn => cn.note === note);
            if (notes.length) return notes[0];
        }

        return null;
    }
    static isCampaignNoteActive(state: IScenarioState, note: string) {
        const campaignNote = this.getCampaignNote(state, note);
        return campaignNote != null && !campaignNote.crossedOut;
    }

    static cardsInLocations(state: IScenarioState): ICard[] {
        let cards: ICard[] = [];
        for (let i = 0; i < state.locations.length; i++) {
            cards = cards.concat(DeckUtil.list(state.locations[i].enemies));
        }
        return cards;
    }
    static getVictoryCards(state: IScenarioState): ICard[] {
        const cards: ICard[] = [];
        //VictoryDisplay
        if (state.victoryDisplay && state.victoryDisplay.cards && state.victoryDisplay.cards.length) {
            for (let i = 0; i < state.victoryDisplay.cards.length; i++) {
                const ctx = state.victoryDisplay.cards[i];
                if (ctx.victoryPoints) {
                    cards.push(({ ...ctx }));
                }
            }
        }
        //all investigated locations
        for (let i = 0; i < state.locations.length; i++) {
            //if location has no victory points, skip it
            if (!state.locations[i].card.victoryPoints) continue;
            //if it isn't flipped, ignore it
            if (state.locations[i].card.faceUp) continue;
            const token = TokenUtil.getToken(state.locations[i].card, GameToken.Clue);
            if (token.number) continue;
            cards.push(({ ...state.locations[i].card }));
        }
        //all defeated monsters
        if (state.discardDeck && state.discardDeck.cards && state.discardDeck.cards.length) {
            for (let i = 0; i < state.discardDeck.cards.length; i++) {
                const ctx = state.discardDeck.cards[i];
                if (ctx.victoryPoints) {
                    cards.push(({ ...ctx }));
                }
            }
        }
        return cards;
    }
    static calculateVictoryPoints(state: IScenarioState): number {
        let vp = 0;
        this.getVictoryCards(state).forEach(c => { vp += c.victoryPoints });
        return vp;
    }
    static assignExperiencePoints(state: IScenarioState, xp: number) {
        for (let i = 0; i < state.investigators.length; i++) {
            state.investigators[i].experience += xp;
        }
    }
    static lookForScenarioCard(state: IScenarioState, cardReference: ICardReference): ICard {
        let card: ICard;
        if (state.extraDecks && state.extraDecks.length) {
            for (let i = 0; i < state.extraDecks.length; i++) {
                card = CardUtil.removeCard(state.extraDecks[i].cards, cardReference);
                if (card) return card;
            }
        }
        for (let i = 0; i < state.locations.length; i++) {
            if (!state.locations[i].enemies) continue;
            card = CardUtil.removeCard(state.locations[i].enemies.cards, cardReference);
            if (card) return card;
        }
        return null;
    }

    static addFreeCard(investigator: IInvestigatorInPlay, cardReference: ICardReference) {
        if (!investigator.freeCards) {
            investigator.freeCards = [];
        }
        investigator.freeCards.push(cardReference);
    }



}
export interface IActState {
    deck: IDeck;
    discard?: IDeck;
}
export interface IAgendaState {
    deck: IDeck;
    discard?: IDeck;
}
export interface IInvestigator {
    card: ICard;
    deck: IDeck;
}

export interface IInvestigatorInPlay extends IInvestigator {
    portrait: ICard;
    hand?: IDeck;
    discard?: IDeck;
    locationId?: number;
    health: number;
    sanity: number;
    activeComponent: PlayerComponent;
    inPlay?: IDeck;
    threat?: IDeck;
    experience: number;
    mentalTrauma: number;
    physicalTrauma: number;
    freeCards?: ICardReference[];
    resigned?: boolean;
    order?: number;
}
export enum PlayerComponent {
    Deck,
    Discard,
    Hand,
    InPlay,
    Threat
}
export enum OtherComponent {
    ScenarioReference,
    EncounterDeck,
    DiscardPile,
    OutOfPlayCards,
    VictoryDisplay
}